import { OnboardingStatusEnum, RoleEnum } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { queryOptionsGetWorkspacesPaginated } from '../api/workspaces/workspaceApi';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { MSR3BaseLayout } from '../layout/MSR3BaseLayout';
import { userService } from '../reducers/permissions/userSlice';
import { useTypedSelector } from '../stateStore';
import ErrorPage from './ErrorPage';
import { NavigateWithParams } from './NavigateWithParams';
import { LoadingFallback } from './QueryResults/LoadingFallback';
import { SingleQueryResults } from './QueryResults/SingleQueryResults';

/**
 * This component is only needed when a route needs to be
 * redirected to a Workspace route.
 *
 * Therefore, it is only needed for '/' and '/workspace'
 */
export const WorkspaceRedirect = ({ children }: React.PropsWithChildren) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userMe = useTypedSelector(userService.selectors.userMe);
  const { workspaceId } = useParams();

  const [, activeWorkspaceQuery] = useActiveWorkspace();
  const paginatedWorkspacesQuery = useQuery(
    queryOptionsGetWorkspacesPaginated(),
  );

  useEffect(() => {
    if (userMe && userMe.roles) {
      for (const role of userMe.roles) {
        if (
          role.roleKey === RoleEnum.WorkspaceOwner &&
          role.onboardingStatus !== OnboardingStatusEnum.Completed
        ) {
          navigate(`/onboarding/${role.workspaceId}/setup-workspace`);
          break;
        }
      }
    }
  }, [userMe, navigate]);

  if (workspaceId && location.pathname.startsWith('/workspace')) {
    return (
      <SingleQueryResults query={activeWorkspaceQuery}>
        {(workspace) => {
          if (workspace) return children ?? <Outlet />;

          return (
            <MSR3BaseLayout>
              <ErrorPage notFound />
            </MSR3BaseLayout>
          );
        }}
      </SingleQueryResults>
    );
  }

  /**
   * Redirect when user lands on '/' or '/workspace'
   * Handles both cases where user either:
   * 1. Belongs to a Workspace, OR
   * 2. Does not belong to any Workspace
   */
  if (
    !workspaceId &&
    (location.pathname === '/' || location.pathname === '/workspace')
  ) {
    return (
      <SingleQueryResults
        notFetched={<LoadingFallback />}
        query={paginatedWorkspacesQuery}
      >
        {(workspaces) => {
          return (
            <NavigateWithParams
              replace
              to={`/workspace/${workspaces.items[0]._id}/home`}
            />
          );
        }}
      </SingleQueryResults>
    );
  }

  // Fallback - should not reach here for valid routes
  return children ?? <Outlet />;
};
